import _ from 'lodash';

import { integerFormatter } from '../../../utilities/FormattingUtilities';

import agent2k from '../../icon/svg/agent2k.svg';
import agent3k from '../../icon/svg/agent3k.svg';
import agent3kg from '../../icon/svg/agent3kg.svg';
import pathEllipsis from '../../icon/svg/path-ellipsis.svg';

export const LATENCY_FORMATTER_CONFIG = {
    baseUnits: 'ms',
    ltSymbol: '<',
    ltComparison: 1,
    ltComparator: (value, config) => _.isFinite(value) && value < config.ltComparison,
};

export function locationFormatter(value, config) {
    if (!value) {
        return config?.emptyValue;
    }

    const latitude = value?.latitude;
    const longitude = value?.longitude;
    const latlon = latitude && longitude ? `(${latitude},${longitude})` : null;

    if (config?.summarize) {
        // Return a summarized location, which will attempt to summarize to 'city, subdivisionIsoCode' if
        // available and will fallback to the most specific location possible if city is not available.
        if (value?.city) {
            let formatted = value?.city;
            if (value?.subdivisionIsoCode) {
                formatted = `${formatted}, ${value?.subdivisionIsoCode}`;
            }
            if (latlon != null) {
                formatted = `${formatted} ${latlon}`;
            }
            return formatted;
        }
        const summary =
            value?.subdivision ||
            value?.subdivisionIsoCode ||
            value?.province ||
            value?.country ||
            value?.isoCode ||
            value?.continent;
        return summary ? null : summary;
    }

    // Return a full location (i.e. 'city, subdivision | subdivisionIsoCode, country | isoCode, continent')
    let fullLocation = _.compact([
        value?.city,
        value?.subdivision || value?.subdivisionIsoCode || value?.province,
        value?.country || value?.isoCode,
        value?.continent,
    ]).join(', ');
    fullLocation = latlon == null ? fullLocation : `${fullLocation} ${latlon}`;
    return fullLocation;
}

function getAgentIcon(data) {
    const agentType = data?.summary?.agent?.type;
    if (agentType === 'V') return 'laptop';
    if (agentType === 'G') return agent3kg;
    return agentType === 'NP-3000H' ? agent3k : agent2k;
}

function getAgentLocation(data) {
    let location = data?.path?.[0]?.location;
    if (!location) {
        location = data?.summary?.source?.sites?.[0];
    }
    return location;
}

function getServerNodeTooltip(data) {
    const mostRelevantSite = data?.summary?.destination?.sites?.[0];
    if (mostRelevantSite?.city || mostRelevantSite?.province || mostRelevantSite?.country) {
        return locationFormatter(mostRelevantSite, { emptyValue: '' });
    }
    return locationFormatter(data?.summary?.destination?.location, { emptyValue: '' });
}

function getSiteOrLocationServerLabel(data) {
    const mostRelevantSite = data?.summary?.destination?.sites?.[0];
    return mostRelevantSite?.name || locationFormatter(data?.summary?.destination?.location, { summarize: true });
}

export function getGraphOptions(test, data) {
    const iconType = 'font';
    const iconWidth = 24;
    const iconHeight = 24;
    const textOffset = iconHeight / 2;
    return {
        nodeTypes: {
            nPoint: {
                name: 'nPoint',
                tooltip: () => locationFormatter(getAgentLocation(data), { emptyValue: '' }),
                iconType: () => (data?.summary?.agent?.type === 'V' ? iconType : 'svg'),
                iconClass: () => (data?.summary?.agent?.type === 'V' ? 'material-icons npoint-icon' : 'npoint-icon'),
                icon: () => getAgentIcon(data),
                iconWidth,
                iconHeight,
                outlineWidth: () =>
                    ['NP-3000H', 'G'].includes(data?.summary?.agent?.type) ? iconWidth : iconWidth / 2,
                outlineHeight: iconHeight,
                textClass: 'npoint-labels',
                textAnchor: 'middle',
                textOffset,
                defaultLabelSource: 'summary.source',
                labels: [() => data?.summary?.agent?.name, () => getAgentLocation(data)?.city || null],
                offset: () => {
                    return {
                        x: data?.summary?.agent?.type === 'V' ? -(iconWidth / 2) : 0,
                        y: 0,
                    };
                },
            },
            Router: {
                name: 'Router',
                iconType,
                iconClass: 'material-icons router-icon',
                icon: 'fiber_manual_record',
                iconWidth: 32,
                iconHeight: 32,
                textClass: 'router-labels',
                textAnchor: 'middle',
                textOffset,
                labels: [(node) => node?.provider || null],
            },
            Server: {
                name: 'Server',
                tooltip: () => getServerNodeTooltip(data),
                iconType,
                iconClass: 'material-icons server-icon',
                icon: () => data?.summary?.test?.testType?.details?.icon || 'public',
                iconWidth,
                iconHeight,
                textClass: 'server-labels',
                textAnchor: 'middle',
                textOffset,
                labels: [() => test?.name || null, () => getSiteOrLocationServerLabel(data)],
                offset: {
                    x: iconWidth / 2,
                    y: 0,
                },
            },
            Disjoint: {
                name: 'Disjoint',
                iconType: 'svg',
                iconClass: 'disjoint-icon',
                icon: pathEllipsis,
                iconWidth,
                iconHeight,
                textClass: 'disjoint-labels',
                textAnchor: 'middle',
                textOffset,
            },
        },
        edgeTypes: {
            Visible: {
                name: 'Visible',
                className: 'visible-edge',
            },
            Hidden: {
                name: 'Hidden',
                className: 'hidden-edge',
            },
        },
    };
}

export function latencyFormatter(value) {
    return integerFormatter(value, LATENCY_FORMATTER_CONFIG);
}

export function getGraphData(data) {
    return {
        nodes: data?.graph?.nodes || [],
        edges:
            data?.graph?.edges?.map((edge) => {
                return {
                    ...edge,
                    type: edge.visible !== false ? 'Visible' : 'Hidden',
                };
            }) || [],
        metrics: [
            {
                title: 'Path Delay',
                value: latencyFormatter(data?.summary?.route?.latency),
                y: 0,
            },
        ],
    };
}
